/**
 * Marketing banner admin service — rewards-service CRUD.
 */

import { api } from '@/app/services/apiClient';
import { API_ENDPOINTS } from '@/app/config/api';
import type {
  CreateMarketingBannerRequest,
  MarketingBannerListResponse,
  MarketingBannerQueryParams,
  MarketingBannerRecord,
  UpdateMarketingBannerRequest,
} from '@/app/types/marketingBanners';

export interface IMarketingBannerService {
  queryBanners(params: MarketingBannerQueryParams): Promise<MarketingBannerListResponse>;
  getBannerById(id: string): Promise<MarketingBannerRecord | null>;
  createBanner(request: CreateMarketingBannerRequest): Promise<MarketingBannerRecord>;
  updateBanner(id: string, request: UpdateMarketingBannerRequest): Promise<MarketingBannerRecord>;
  deleteBanner(id: string): Promise<boolean>;
}

function buildQueryParams(params: MarketingBannerQueryParams): Record<string, string | number> {
  const query: Record<string, string | number> = {};
  if (params.status != null) query.status = params.status;
  if (params.search != null && params.search.trim() !== '') query.search = params.search.trim();
  if (params.limit != null) query.limit = params.limit;
  if (params.offset != null) query.offset = params.offset;
  return query;
}

const apiMarketingBannerService: IMarketingBannerService = {
  async queryBanners(params: MarketingBannerQueryParams): Promise<MarketingBannerListResponse> {
    const response = await api.get<MarketingBannerListResponse>(API_ENDPOINTS.admin.marketingBanners, {
      params: buildQueryParams(params),
    });
    return response.data;
  },

  async getBannerById(id: string): Promise<MarketingBannerRecord | null> {
    try {
      const response = await api.get<MarketingBannerRecord>(
        API_ENDPOINTS.admin.marketingBannerDetail(id),
      );
      return response.data;
    } catch (err: unknown) {
      if (typeof err === 'object' && err !== null && 'response' in err) {
        const status = (err as { response?: { status?: number } }).response?.status;
        if (status === 404) return null;
      }
      throw err;
    }
  },

  async createBanner(request: CreateMarketingBannerRequest): Promise<MarketingBannerRecord> {
    const response = await api.post<MarketingBannerRecord>(
      API_ENDPOINTS.admin.marketingBanners,
      request,
    );
    return response.data;
  },

  async updateBanner(
    id: string,
    request: UpdateMarketingBannerRequest,
  ): Promise<MarketingBannerRecord> {
    const response = await api.put<MarketingBannerRecord>(
      API_ENDPOINTS.admin.marketingBannerDetail(id),
      request,
    );
    return response.data;
  },

  async deleteBanner(id: string): Promise<boolean> {
    try {
      await api.delete(API_ENDPOINTS.admin.marketingBannerDetail(id));
      return true;
    } catch (err: unknown) {
      if (typeof err === 'object' && err !== null && 'response' in err) {
        const status = (err as { response?: { status?: number } }).response?.status;
        if (status === 404) return false;
      }
      throw err;
    }
  },
};

export const marketingBannerService: IMarketingBannerService = apiMarketingBannerService;
